import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {logoutAction} from '../rootAction';
import {SelectedClientState, setClientData} from './selectedClientSlice';

export interface SelectionListItemType {
  id: string;
  name: string;
  code?: string;
  parentId?: string;
  isNonConsilioWorkflow?: boolean;
}

interface WorkspaceType {
  workspaceId: string;
  workspaceCode: string;
  name: string;
}
interface DialogProjectWorkSpace {
  projectId: string;
  projectName: string;
  projectCode: string;
  projectStatus: string;
  workspaces: WorkspaceType[];
  isNonConsilioWorkflow: boolean;
}

interface DialogState {
  isOpen: boolean;
  isClientsOpen: boolean;
  hasContinued: boolean;
  projectWorkSpaceList: DialogProjectWorkSpace[];
  openCount: number;
  dialogReportName: string;
  isProjectLoading: boolean;
  clientId?: string | null;
  projectList: SelectionListItemType[];
  workspaceList: SelectionListItemType[];
  projectWorkSpaceMap: {[key: string]: SelectionListItemType[]};
}

const initialState: DialogState = {
  isOpen: false,
  isClientsOpen: false,
  hasContinued: false,
  projectWorkSpaceList: [],
  openCount: 0,
  dialogReportName: '',
  isProjectLoading: false,
  projectList: [],
  workspaceList: [],
  projectWorkSpaceMap: {},
};

const dialogSlice = createSlice({
  name: 'dialog',
  initialState,
  reducers: {
    openDialog: (state) => {
      state.isOpen = true;
    },
    closeDialog: (state) => {
      state.isOpen = false;
      state.hasContinued = false;
    },
    continueDialog: (state) => {
      state.hasContinued = true;
    },
    continueNCloseDialog: (state) => {
      state.hasContinued = true;
      state.isOpen = false;
    },
    setProjectWorkSpaceListData(
      state,
      action: PayloadAction<DialogProjectWorkSpace[]>
    ) {
      const {projectList, workspaceList, projectWorkspaceMap} =
        getProjectWorkspaceList(action.payload);
      state.projectWorkSpaceList = action.payload;
      state.projectList = projectList;
      state.workspaceList = workspaceList;
      state.projectWorkSpaceMap = projectWorkspaceMap;
    },
    openClientsDialog: (state) => {
      state.isClientsOpen = true;
    },
    closeClientsDialog: (state) => {
      state.isClientsOpen = false;
      state.hasContinued = false;
    },
    setOpenCount: (state, action: PayloadAction<number>) => {
      state.openCount = action.payload;
    },
    setDialogReportName: (state, action: PayloadAction<string>) => {
      state.dialogReportName = action.payload;
    },
    setIsProjectLoading: (state, action: PayloadAction<boolean>) => {
      state.isProjectLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => {
      return initialState;
    });

    builder.addCase(
      setClientData,
      (state, action: PayloadAction<SelectedClientState>) => {
        if (state.clientId !== action.payload.clientId) {
          state.projectWorkSpaceList = [];
          state.dialogReportName = '';
          state.isProjectLoading = false;
          state.isOpen = false;
          state.projectList = [];
          state.workspaceList = [];
          state.projectWorkSpaceMap = {};
        }
        state.clientId = action.payload.clientId;
      }
    );
  },
});

export const getProjectWorkspaceList = (data: DialogProjectWorkSpace[]) => {
  const projectList: SelectionListItemType[] = [];
  const workspaceList: SelectionListItemType[] = [];
  const projectWorkspaceMap: {[key: string]: SelectionListItemType[]} = {};

  for (const project of data) {
    projectList.push({
      name: project.projectName,
      id: project.projectId,
      code: project.projectCode,
      isNonConsilioWorkflow: project.isNonConsilioWorkflow,
    });
    const workspaces: SelectionListItemType[] = (
      project.workspaces ?? []
    ).reduce((result: SelectionListItemType[], item: WorkspaceType) => {
      result.push({
        name: item.name as string,
        id: item.workspaceId as string,
        code: item.workspaceCode as string,
        parentId: project.projectId as string,
      });
      return result;
    }, []);
    projectWorkspaceMap[project.projectId as string] = workspaces;
    workspaceList.push(...workspaces);
  }
  return {projectList, workspaceList, projectWorkspaceMap};
};

export const {
  openDialog,
  closeDialog,
  continueDialog,
  setProjectWorkSpaceListData,
  openClientsDialog,
  closeClientsDialog,
  continueNCloseDialog,
  setOpenCount,
  setDialogReportName,
  setIsProjectLoading,
} = dialogSlice.actions;
export default dialogSlice.reducer;
